import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import Button from 'react-bootstrap/Button';

import { sendAuthenticatedAsyncRequest } from 'services/AsyncRequestService';
import DismissableModal from 'components/DismissableModal';
import { saveWorkingCodexInfo } from 'views/Cat/CodexVersion/CodexVersion.actions';
import CAT_CONSTANTS from 'lookup/CatConstants';
import { getErrorMessage } from 'services/UtilityService';
import { showNotification } from 'services/NotificationService';

const lookup = {
  ACTIVE: {
    preventAction: true,
    messageOnAttempt: 'Cannot change state of active version!',
  },
  IN_DEVELOPMENT: {
    modalBody: workingCodexVersion => 
      <p>
        Are you sure you want to <strong>Approve</strong> changes made in Codex <strong>V {workingCodexVersion}</strong>? <br /> 
        <span className='text-muted'>Another CT member needs to approve the Codex for testing.</span>
      </p>,
    nextStatus: CAT_CONSTANTS.codexStatuses.APPROVED,
    openModalButtonText: 'Approve Codex',
    openModalButtonVariant: 'warning'
  },
  APPROVED_REVERSE: {
    modalBody: workingCodexVersion => 
    <p>
      Are you sure you want to revert the codex <strong>V {workingCodexVersion}</strong> back to <strong>Development</strong> state? <br /> 
      <span className='text-muted'>Doing so will allow the users to make and commit changes to the codex.</span>
    </p>,
    nextStatus: CAT_CONSTANTS.codexStatuses.IN_DEVELOPMENT,
    openModalButtonText: 'Reopen for Development',
    openModalButtonVariant: 'outline-danger',
  },
  PARTIALLY_APPROVED: {
    modalBody: workingCodexVersion => 
      <p>
        Are you sure you want to <strong>Approve</strong> the codex <strong>V {workingCodexVersion}</strong>? <br /> 
        <span className='text-muted'>Doing so will lock the codex for testing.</span>
      </p>,
    nextStatus: CAT_CONSTANTS.codexStatuses.APPROVED,
    openModalButtonText: 'Approve Codex',
    openModalButtonVariant: 'warning',
    reverseAction: 'APPROVED_REVERSE' // must be a key in the same lookup
  },
  APPROVED: {
    modalBody: workingCodexVersion => 
      <p>
        Are you sure you want to <strong>Publish</strong> the codex <strong>V {workingCodexVersion}</strong>? <br /> 
        <span className='text-muted'>Doing so will publish the codex in production and will mark all previous versions as archived.</span>
      </p>,
    nextStatus: CAT_CONSTANTS.codexStatuses.ACTIVE,
    openModalButtonText: 'Publish Codex',
    openModalButtonVariant: 'danger',
    reverseAction: 'APPROVED_REVERSE' // must be a key in the same lookup
  },
  ARCHIVED: {
    preventAction: true,
    messageOnAttempt: 'Cannot change state of archived version!'
  }
};

const CodexStatusModal = ({ lookupKey: lookupKeyProp, disabledButton }) => {
  const dispatch = useDispatch();
  const modalRef = useRef(null);
  const [loading, setLoading] = useState(false);

  const {
    workingCodexVersion,
    workingCodexVersionId,
    workingCodexVersionStatus,
  } = useSelector((state) => ({
    workingCodexVersion: state.codexVersion
      ? state.codexVersion.codexVersion
      : null,
    workingCodexVersionStatus: state.codexVersion
      ? state.codexVersion.codexVersionStatus
      : null,
    workingCodexVersionId: state.codexVersion
      ? state.codexVersion.codexVersionId
      : null,
  }));

  const lookupKey = useMemo(
    () => lookupKeyProp || workingCodexVersionStatus,
    [lookupKeyProp, workingCodexVersionStatus]
  );

  const changeCodexStatus = useCallback(
    (closeModal) => {
      if (!lookupKey || !lookup[lookupKey].nextStatus) {
      showNotification({
        title: 'Action not allowed!',
          message: lookup[lookupKey]
            ? lookup[lookupKey].messageOnAttempt
            : 'Cannot change codex status.',
        position: 'tr',
          type: 'error',
      });
      return;
    }

      setLoading(true);
    sendAuthenticatedAsyncRequest(
      `/cat/codex-version/${workingCodexVersionId}/status/${lookup[lookupKey].nextStatus}`,
      'PUT', 
      null,
      (r) => {
          const {
            version,
            codexVersionId,
            creationDate,
            tag,
            status,
            firstApprovalBy,
          } = r.data;
          dispatch(
            saveWorkingCodexInfo(
              version,
              codexVersionId,
              creationDate,
              tag,
              status,
              firstApprovalBy
            )
          );
          if (closeModal) {
        closeModal();
          }
          setLoading(false);
        showNotification({
            title: 'Codex Status Updated Successfully!',
          message: 'The codex status has been updated successfully',
          position: 'tr',
            type: 'success',
        });
      },
      (r) => {
          setLoading(false);
        showNotification({
          title: 'Something went wrong!',
            message:
              getErrorMessage(r.data) ||
              getErrorMessage('UNABLE_TO_CHANGE_CODEX_STATUS'),
          position: 'tr',
            type: 'error',
        });
        }
    );
    },
    [dispatch, lookupKey, workingCodexVersionId]
  );

  if (!lookupKey || lookup[lookupKey].preventAction) {
    return null;
  }

  const title = 'Change Codex Status';

  const handleOpen = () => {
    if (modalRef.current && modalRef.current.handleShow) {
      modalRef.current.handleShow();
    }
  };

  const handleClose = () => {
    if (modalRef.current && modalRef.current.handleClose) {
      modalRef.current.handleClose();
    }
  };

  const modalOpener = (
    <Button
      size="sm"
      style={{ float: 'right', marginRight: 5 }}
      key={1}
        variant={lookup[lookupKey].openModalButtonVariant || 'outline-light'}  
      onClick={handleOpen}
      disabled={disabledButton}
    >
          {lookup[lookupKey].openModalButtonText || 'Change Codex Status'}
    </Button>
  );

    const customFooterContent = (
      <div>
      <Button
        variant="outline-dark"
        onClick={handleClose}
        disabled={loading}
      >
        Cancel
      </Button>
        <Button 
          className="left-spacer" 
          variant={lookup[lookupKey].openModalButtonVariant || 'warning'}
          disabled={loading}
        onClick={() => changeCodexStatus(handleClose)}
      >
        {loading
          ? 'Updating Codex Status ...'
          : lookup[lookupKey].openModalButtonText || 'Change Codex Status'}
        </Button>
      </div>
    );

    return (
      <>
      <DismissableModal
        ref={modalRef}
        openModalButton={modalOpener}
        title={title}
        customFooterContent={customFooterContent}
        >
        {lookup[lookupKey].modalBody(workingCodexVersion)}
      </DismissableModal>
      {lookup[lookupKey].reverseAction ? (
        <CodexStatusModal lookupKey={lookup[lookupKey].reverseAction} />
      ) : null}
      </>
    );
};

export default CodexStatusModal;